import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api } from '../api/client';

interface ImportSummary {
  totalRows: number;
  inserted: number;
  updated: number;
  totalUsd: number;
  byEndpoint: Array<{ endpoint: string; calls: number; totalUsd: number }>;
  dateRange: { from: string | null; to: string | null };
  source: 'csv' | 'api';
}

interface Reconciliation {
  range: { from: string | null; to: string | null };
  totalRealUsd: number;
  totalRealUsdXvision: number;
  totalEstimatedUsd: number;
  overallDriftPct: number;
  overallDriftPctXvision: number;
  rows: Array<{
    endpoint: string;
    realUsd: number;
    estimatedUsd: number;
    driftPct: number;
    csvRows: number;
    trackedCalls: number;
    isXvision: boolean;
  }>;
}

interface Balance {
  username: string;
  currentBalanceUsd: number | null;
  currency: string;
  lastAutoSyncAt: string | null;
}

/**
 * Página `/billing` — importar CSV de fal.ai (Account → Usage → Export) y comparar
 * costos reales vs nuestro tracking estimado.
 */
export function BillingPage() {
  const [csvText, setCsvText] = useState('');
  const [fileName, setFileName] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportSummary | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [reconcile, setReconcile] = useState<Reconciliation | null>(null);
  const [reconcileLoading, setReconcileLoading] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [xvisionOnly, setXvisionOnly] = useState(true); // default ON — el usuario casi siempre quiere ver solo lo de la app
  const [syncing, setSyncing] = useState(false);
  const [balance, setBalance] = useState<Balance | null>(null);

  const loadBalance = async () => {
    try {
      const b = await api.get<Balance>('/admin/fal-billing/balance');
      setBalance(b);
    } catch {
      setBalance(null);
    }
  };

  // Cargar balance al montar
  useEffect(() => {
    loadBalance();
  }, []);

  const doSync = async () => {
    setSyncing(true);
    setImportError(null);
    setImportResult(null);
    try {
      const res = await api.post<ImportSummary>('/admin/fal-billing/sync', {});
      setImportResult(res);
      if (res.dateRange.from && res.dateRange.to) {
        setFrom(res.dateRange.from.slice(0, 10));
        setTo(res.dateRange.to.slice(0, 10));
        await doReconcile(res.dateRange.from, res.dateRange.to);
      }
      await loadBalance();
    } catch (err) {
      setImportError(err instanceof Error ? err.message : String(err));
    } finally {
      setSyncing(false);
    }
  };

  const onFile = (file: File) => {
    setFileName(file.name);
    file.text().then(setCsvText);
  };

  const doImport = async () => {
    setImporting(true);
    setImportError(null);
    setImportResult(null);
    try {
      const res = await api.post<ImportSummary>('/admin/fal-billing/import', { csv: csvText });
      setImportResult(res);
      // Disparar reconcile automático con el rango del CSV recién importado
      if (res.dateRange.from && res.dateRange.to) {
        setFrom(res.dateRange.from.slice(0, 10));
        setTo(res.dateRange.to.slice(0, 10));
        await doReconcile(res.dateRange.from, res.dateRange.to);
      }
    } catch (err) {
      setImportError(err instanceof Error ? err.message : String(err));
    } finally {
      setImporting(false);
    }
  };

  const doReconcile = async (fromOverride?: string, toOverride?: string) => {
    setReconcileLoading(true);
    try {
      const params = new URLSearchParams();
      const f = fromOverride ?? from;
      const t = toOverride ?? to;
      if (f) params.set('from', f);
      if (t) params.set('to', t);
      if (xvisionOnly) params.set('xvisionOnly', 'true');
      const res = await api.get<Reconciliation>(
        `/admin/fal-billing/reconcile${params.toString() ? `?${params}` : ''}`,
      );
      setReconcile(res);
    } catch (err) {
      setImportError(err instanceof Error ? err.message : String(err));
    } finally {
      setReconcileLoading(false);
    }
  };

  // Re-disparar reconcile cuando cambia el toggle xvisionOnly
  useEffect(() => {
    if (reconcile) doReconcile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [xvisionOnly]);

  return (
    <div className="p-3 sm:p-6 max-w-6xl mx-auto">
      <header className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-2xl font-bold text-slate-100">💰 Billing fal.ai</h1>
          <div className="flex items-center gap-3 text-xs">
            {balance && (
              <span className="text-slate-300">
                <span className="text-slate-500">{balance.username} ·</span>{' '}
                <strong className={balance.currentBalanceUsd != null && balance.currentBalanceUsd < 5 ? 'text-amber-400' : 'text-emerald-400'}>
                  {balance.currentBalanceUsd != null
                    ? `$${balance.currentBalanceUsd.toFixed(2)} ${balance.currency}`
                    : 'saldo n/d'}
                </strong>
              </span>
            )}
            <Link to="/projects" className="text-slate-400 hover:text-vision underline">
              ← volver a proyectos
            </Link>
          </div>
        </div>
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <p className="text-sm text-slate-400">
            Sincronizá costos reales de fal.ai (via API directa o CSV) y reconcilialos contra
            nuestro tracking interno para calibrar el catálogo de modelos.
          </p>
          {balance?.lastAutoSyncAt && (
            <span className="text-[10px] text-slate-500 italic">
              ⏱ auto-sync activo cada 5 min · último: {new Date(balance.lastAutoSyncAt).toLocaleTimeString()}
            </span>
          )}
        </div>
      </header>

      <section className="bg-slate-900 border border-slate-700 rounded-lg p-4 mb-6">
        <h2 className="text-sm uppercase text-slate-400 mb-3">🔄 Sincronización</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Sync via API directa (preferido) */}
          <div className="bg-slate-950 border border-slate-800 rounded p-3 space-y-2">
            <div className="text-xs text-slate-300 font-medium">⚡ API directa (preferido)</div>
            <div className="text-[10px] text-slate-500">
              Llama a <code>/v1/models/usage</code> con la <code>FAL_ADMIN_KEY</code>. Sin descargar archivos.
            </div>
            <button
              onClick={doSync}
              disabled={syncing}
              className="w-full px-3 py-2 text-sm rounded bg-vision-dark hover:bg-vision text-white disabled:opacity-40"
            >
              {syncing ? 'Sincronizando…' : 'Sincronizar últimos 30 días'}
            </button>
          </div>

          {/* Upload CSV (fallback) */}
          <div className="bg-slate-950 border border-slate-800 rounded p-3 space-y-2">
            <div className="text-xs text-slate-300 font-medium">📥 Upload CSV (fallback)</div>
            <div className="text-[10px] text-slate-500">
              Bajalo de <code>fal.ai/dashboard/usage → Export</code>.
            </div>
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])}
              className="block w-full text-xs text-slate-300 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-slate-800 file:text-slate-200 hover:file:bg-slate-700 file:cursor-pointer"
            />
            {fileName && (
              <div className="text-[10px] text-slate-500 truncate">
                {fileName} · {csvText.split('\n').length - 1} filas
              </div>
            )}
            <button
              onClick={doImport}
              disabled={!csvText || importing}
              className="w-full px-3 py-2 text-sm rounded bg-slate-800 hover:bg-slate-700 text-slate-200 disabled:opacity-40"
            >
              {importing ? 'Importando…' : 'Importar CSV'}
            </button>
          </div>
        </div>

        <div className="mt-3 space-y-2">

          {importError && (
            <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded px-3 py-2">
              {importError}
            </div>
          )}

          {importResult && (
            <div className="text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded px-3 py-2 space-y-1">
              <div>
                ✅ {importResult.source === 'api' ? 'API sync' : 'CSV import'}: {importResult.inserted} nuevas + {importResult.updated} actualizadas
                ({importResult.totalRows} filas)
              </div>
              <div>
                💰 Total: <strong>${importResult.totalUsd.toFixed(2)}</strong>
                {importResult.dateRange.from && (
                  <> · {importResult.dateRange.from.slice(0, 10)} → {importResult.dateRange.to?.slice(0, 10)}</>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="bg-slate-900 border border-slate-700 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h2 className="text-sm uppercase text-slate-400">📊 Reconciliación real vs estimado</h2>
          <div className="flex items-center gap-2 text-xs flex-wrap">
            <label className="flex items-center gap-1.5 text-slate-300 cursor-pointer">
              <input
                type="checkbox"
                checked={xvisionOnly}
                onChange={(e) => setXvisionOnly(e.target.checked)}
                className="accent-vision"
              />
              solo xVision
            </label>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="bg-slate-950 border border-slate-700 rounded px-2 py-1 text-slate-200"
            />
            <span className="text-slate-500">→</span>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="bg-slate-950 border border-slate-700 rounded px-2 py-1 text-slate-200"
            />
            <button
              onClick={() => doReconcile()}
              disabled={reconcileLoading}
              className="px-3 py-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-200 disabled:opacity-40"
            >
              {reconcileLoading ? '…' : 'Calcular'}
            </button>
          </div>
        </div>

        {!reconcile ? (
          <div className="text-xs text-slate-500 italic">
            Importá un CSV para ver el análisis, o elegí rango de fechas y apretá Calcular.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <Stat label="💰 Real (CSV)" value={`$${reconcile.totalRealUsd.toFixed(2)}`} />
              <Stat label="📊 Estimado (interno)" value={`$${reconcile.totalEstimatedUsd.toFixed(2)}`} />
              <Stat
                label="↻ Drift"
                value={`${reconcile.overallDriftPct >= 0 ? '+' : ''}${reconcile.overallDriftPct}%`}
                color={
                  Math.abs(reconcile.overallDriftPct) < 10
                    ? 'emerald'
                    : Math.abs(reconcile.overallDriftPct) < 30
                      ? 'amber'
                      : 'red'
                }
              />
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-slate-400 uppercase border-b border-slate-800">
                  <tr>
                    <th className="text-left py-2 px-2">Endpoint</th>
                    <th className="text-right py-2 px-2">Real</th>
                    <th className="text-right py-2 px-2">Estimado</th>
                    <th className="text-right py-2 px-2">Drift</th>
                    <th className="text-right py-2 px-2">CSV rows</th>
                    <th className="text-right py-2 px-2">Calls tracked</th>
                  </tr>
                </thead>
                <tbody>
                  {reconcile.rows.map((row) => (
                    <tr key={row.endpoint} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                      <td className="py-1.5 px-2 font-mono text-slate-300">{row.endpoint}</td>
                      <td className="py-1.5 px-2 text-right text-slate-200">
                        ${row.realUsd.toFixed(4)}
                      </td>
                      <td className="py-1.5 px-2 text-right text-slate-400">
                        ${row.estimatedUsd.toFixed(4)}
                      </td>
                      <td
                        className={`py-1.5 px-2 text-right ${
                          Math.abs(row.driftPct) < 10
                            ? 'text-emerald-400'
                            : Math.abs(row.driftPct) < 30
                              ? 'text-amber-400'
                              : 'text-red-400'
                        }`}
                      >
                        {row.realUsd === 0 && row.estimatedUsd === 0
                          ? '—'
                          : row.realUsd === 0
                            ? 'N/A (no real)'
                            : `${row.driftPct >= 0 ? '+' : ''}${row.driftPct}%`}
                      </td>
                      <td className="py-1.5 px-2 text-right text-slate-500">{row.csvRows}</td>
                      <td className="py-1.5 px-2 text-right text-slate-500">{row.trackedCalls}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="text-[10px] text-slate-600 italic mt-3 leading-relaxed">
              Drift = (estimado - real) / real × 100. Negativo = sobre-estimamos. Positivo =
              sub-estimamos. &lt;10% verde, &lt;30% ámbar, ≥30% rojo. Cuando un endpoint
              tiene calls tracked = 0 pero CSV rows &gt; 0, son llamadas fuera de xVision
              (probablemente probadas a mano en el playground de fal.ai).
            </div>
          </>
        )}
      </section>
    </div>
  );
}

function Stat({ label, value, color }: { label: string; value: string; color?: 'emerald' | 'amber' | 'red' }) {
  const colorClass =
    color === 'emerald' ? 'text-emerald-400' : color === 'amber' ? 'text-amber-400' : color === 'red' ? 'text-red-400' : 'text-slate-200';
  return (
    <div className="bg-slate-950 border border-slate-800 rounded p-3">
      <div className="text-[10px] uppercase text-slate-500">{label}</div>
      <div className={`text-2xl font-semibold ${colorClass}`}>{value}</div>
    </div>
  );
}
